import * as THREE from 'three';
import type { MaterialProfile } from '../types';

const TEX = 384;

function makeCanvas(): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = TEX;
  return c;
}

function getCtx(canvas: HTMLCanvasElement, opts?: { readback?: boolean }): CanvasRenderingContext2D {
  return canvas.getContext('2d', opts?.readback ? { willReadFrequently: true } : undefined)!;
}

function rng(seed: number) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

export function buildAlbedoCanvas(profile: MaterialProfile, seed: number, baseHex?: string): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = getCtx(c, { readback: true });
  const r = rng(seed);
  const base = baseColorFor(profile, baseHex);
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, TEX, TEX);

  if (profile === 'wood') {
    const tone = colorTone(base);
    const whiteLaminate = tone.l > 0.72 && tone.s < 0.24;
    const lineAlpha = whiteLaminate ? 0.004 : 0.007;
    const waveAlpha = whiteLaminate ? 0.006 : 0.01;

    // ── ПЛАНКИ: полосуем ДО волокон. 4 доски на канвас со смещением тона
    // каждая — иначе «паркет» читается как одна бесшовная доска длиной в
    // комнату, и пол выглядит пластиковой плёнкой. Канвас ≈1.1 м → планка
    // ≈280 мм: честная ширина инженерной доски.
    const planks = 4;
    const plankTones: number[] = [];
    for (let p = 0; p < planks; p += 1) plankTones.push((r() - 0.5) * (whiteLaminate ? 0.05 : 0.085));
    for (let y = 0; y < TEX; y += 1) {
      const p = Math.min(planks - 1, Math.floor((y / TEX) * planks));
      const t = plankTones[p];
      ctx.fillStyle = whiteLaminate
        ? `rgba(${t > 0 ? 255 : 70},${t > 0 ? 255 : 74},${t > 0 ? 255 : 80},${Math.abs(t)})`
        : `rgba(${t > 0 ? 255 : 46},${t > 0 ? 244 : 26},${t > 0 ? 230 : 12},${Math.abs(t)})`;
      ctx.fillRect(0, y, TEX, 1);
    }
    for (let p = 1; p < planks; p += 1) {
      const y = Math.round((p / planks) * TEX);
      const seam = ctx.createLinearGradient(0, y - 1.6, 0, y + 1.6);
      seam.addColorStop(0, 'rgba(0,0,0,0)');
      seam.addColorStop(0.5, whiteLaminate ? 'rgba(72,76,80,0.34)' : 'rgba(38,22,8,0.4)');
      seam.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = seam;
      ctx.fillRect(0, y - 2, TEX, 4);
    }

    const grad = ctx.createLinearGradient(0, 0, TEX, 0);
    grad.addColorStop(0, whiteLaminate ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.055)');
    grad.addColorStop(0.42, whiteLaminate ? 'rgba(85,88,92,0.018)' : 'rgba(91,58,28,0.05)');
    grad.addColorStop(0.74, 'rgba(255,255,255,0.04)');
    grad.addColorStop(1, whiteLaminate ? 'rgba(80,82,86,0.018)' : 'rgba(54,34,18,0.062)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, TEX, TEX);

    for (let x = 0; x < TEX; x += 1) {
      const noise = lineAlpha * 0.5 + r() * lineAlpha;
      ctx.fillStyle = whiteLaminate ? `rgba(80,86,92,${noise})` : `rgba(55,34,16,${noise})`;
      ctx.fillRect(x, 0, 1, TEX);
    }
    const grainLines = whiteLaminate ? 6 : 8;
    for (let i = 0; i < grainLines; i += 1) {
      ctx.strokeStyle = whiteLaminate
        ? `rgba(72,78,84,${0.006 + r() * waveAlpha})`
        : `rgba(64,39,16,${0.01 + r() * waveAlpha})`;
      ctx.lineWidth = whiteLaminate ? 0.22 + r() * 0.28 : 0.28 + r() * 0.38;
      ctx.beginPath();
      const cx = r() * TEX;
      ctx.moveTo(cx, -4);
      const wave = whiteLaminate ? 0.45 + r() * 1.15 : 1.4 + r() * 3.4;
      const phase = r() * Math.PI * 2;
      for (let y = 0; y <= TEX; y += 8) {
        ctx.lineTo(cx + Math.sin(y / 24 + phase) * wave + Math.sin(y / 9 + phase) * 0.55, y);
      }
      ctx.stroke();
    }
    if (!whiteLaminate) {
      for (let i = 0; i < 24; i += 1) {
        const y = r() * TEX;
        const x = r() * TEX;
        ctx.fillStyle = `rgba(255,255,255,${0.004 + r() * 0.006})`;
        ctx.fillRect(x, y, 0.2 + r() * 0.45, 0.2 + r() * 0.6);
      }
      for (let i = 0; i < 1; i += 1) {
        const cx = r() * TEX;
        const cy = r() * TEX;
        const rx = 4 + r() * 10;
        const ry = 14 + r() * 28;
        ctx.strokeStyle = `rgba(66,40,17,${0.01 + r() * 0.012})`;
        ctx.lineWidth = 0.25 + r() * 0.22;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, (r() - 0.5) * 0.18, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  } else if (profile === 'stone') {
    // ── ПЛИТКА 2×1 НА КАНВАС (≈600×1200 мм при канвасе ~1.1 м): тон каждой
    // плитки чуть свой + шов. Без сетки камень читается серой кашей — это
    // был главный источник «дешёвого» пола на дефолтной сцене.
    const cols = 2, rows = 1;
    const tileW = TEX / cols, tileH = TEX / rows;
    for (let tc = 0; tc < cols; tc += 1) {
      for (let tr = 0; tr < rows; tr += 1) {
        const t = (r() - 0.5) * 0.09;
        ctx.fillStyle = t > 0 ? `rgba(255,255,255,${t})` : `rgba(30,30,34,${-t})`;
        ctx.fillRect(tc * tileW, tr * tileH, tileW, tileH);
        // лёгкая диагональная жила керамики, своя у каждой плитки
        ctx.save();
        ctx.beginPath();
        ctx.rect(tc * tileW, tr * tileH, tileW, tileH);
        ctx.clip();
        ctx.strokeStyle = `rgba(${60 + r() * 40 | 0},${60 + r() * 40 | 0},${64 + r() * 40 | 0},${0.05 + r() * 0.07})`;
        ctx.lineWidth = 0.6 + r() * 1.1;
        ctx.beginPath();
        const vx = tc * tileW + r() * tileW;
        ctx.moveTo(vx, tr * tileH - 6);
        for (let y = 0; y <= tileH + 12; y += 10) {
          ctx.lineTo(vx + Math.sin(y / 34 + tc * 2.1) * (5 + r() * 9), tr * tileH + y);
        }
        ctx.stroke();
        ctx.restore();
      }
    }
    // швы: тёмная линия с полупрозрачными краями — рельеф даст normalMap
    ctx.strokeStyle = 'rgba(46,46,50,0.42)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(0.75, 0.75, TEX - 1.5, TEX - 1.5);
    for (let tc = 1; tc < cols; tc += 1) {
      const x = Math.round(tc * tileW);
      const seam = ctx.createLinearGradient(x - 1.6, 0, x + 1.6, 0);
      seam.addColorStop(0, 'rgba(46,46,50,0)');
      seam.addColorStop(0.5, 'rgba(46,46,50,0.46)');
      seam.addColorStop(1, 'rgba(46,46,50,0)');
      ctx.fillStyle = seam;
      ctx.fillRect(x - 2, 0, 4, TEX);
    }
    for (let i = 0; i < 1800; i += 1) {
      ctx.fillStyle = `rgba(${50 + r() * 60 | 0},${50 + r() * 60 | 0},${50 + r() * 60 | 0},${0.04 + r() * 0.1})`;
      ctx.fillRect(r() * TEX, r() * TEX, 1 + r() * 1.4, 1 + r() * 1.4);
    }
    for (let i = 0; i < 14; i += 1) {
      ctx.strokeStyle = `rgba(40,40,40,${0.05 + r() * 0.08})`;
      ctx.lineWidth = 0.4 + r() * 0.5;
      ctx.beginPath();
      const cy = r() * TEX;
      ctx.moveTo(0, cy);
      for (let x = 0; x <= TEX; x += 4) ctx.lineTo(x, cy + Math.sin((x + i * 9) / 18) * 5 * r());
      ctx.stroke();
    }
  } else if (profile === 'metal') {
    for (let y = 0; y < TEX; y += 1) {
      ctx.fillStyle = `rgba(255,255,255,${0.04 + r() * 0.08})`;
      ctx.fillRect(0, y, TEX, 1);
    }
    for (let i = 0; i < 180; i += 1) {
      ctx.strokeStyle = `rgba(255,255,255,${0.03 + r() * 0.06})`;
      ctx.lineWidth = 0.3;
      ctx.beginPath();
      const y = r() * TEX;
      ctx.moveTo(0, y);
      ctx.lineTo(TEX, y + (r() - 0.5) * 4);
      ctx.stroke();
    }
  } else if (profile === 'paint' || profile === 'appliance') {
    const speckles = profile === 'paint' ? 1900 : 1300;
    const baseAlpha = profile === 'paint' ? 0.018 : 0.012;
    const alphaSpread = profile === 'paint' ? 0.045 : 0.03;
    for (let i = 0; i < speckles; i += 1) {
      ctx.fillStyle = `rgba(0,0,0,${baseAlpha + r() * alphaSpread})`;
      ctx.beginPath();
      ctx.arc(r() * TEX, r() * TEX, 0.5 + r() * 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
    if (profile === 'paint') {
      for (let i = 0; i < 28; i += 1) {
        const y = r() * TEX;
        const grad = ctx.createLinearGradient(0, y - 12, TEX, y + 12);
        grad.addColorStop(0, `rgba(255,255,255,${0.012 + r() * 0.018})`);
        grad.addColorStop(0.5, `rgba(70,68,63,${0.014 + r() * 0.026})`);
        grad.addColorStop(1, `rgba(255,255,255,${0.01 + r() * 0.014})`);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 0.8 + r() * 2.4;
        ctx.beginPath();
        ctx.moveTo(-8, y);
        for (let x = 0; x <= TEX + 8; x += 12) {
          ctx.lineTo(x, y + Math.sin((x + i * 17) / 36) * (2 + r() * 3));
        }
        ctx.stroke();
      }
    }
  } else if (profile === 'fabric') {
    for (let i = 0; i < TEX; i += 3) {
      ctx.fillStyle = `rgba(0,0,0,${0.05 + r() * 0.05})`;
      ctx.fillRect(i, 0, 1, TEX);
      ctx.fillRect(0, i, TEX, 1);
    }
  } else if (profile === 'glass') {
    ctx.fillStyle = 'rgba(255,255,255,0.12)';
    ctx.fillRect(0, 0, TEX, TEX);
    for (let i = 0; i < 45; i += 1) {
      ctx.strokeStyle = `rgba(255,255,255,${0.02 + r() * 0.04})`;
      ctx.lineWidth = 0.4;
      ctx.beginPath();
      ctx.moveTo(r() * TEX, 0);
      ctx.lineTo(r() * TEX, TEX);
      ctx.stroke();
    }
  }

  return c;
}

export function buildNormalCanvas(albedo: HTMLCanvasElement, strength = 2.1): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = getCtx(c);
  const src = getCtx(albedo, { readback: true }).getImageData(0, 0, TEX, TEX);
  const dst = ctx.createImageData(TEX, TEX);
  const lum = (i: number) => 0.2126 * src.data[i] + 0.7152 * src.data[i + 1] + 0.0722 * src.data[i + 2];
  for (let y = 0; y < TEX; y += 1) {
    for (let x = 0; x < TEX; x += 1) {
      const i = (y * TEX + x) * 4;
      const xm = ((x - 1 + TEX) % TEX);
      const xp = ((x + 1) % TEX);
      const ym = ((y - 1 + TEX) % TEX);
      const yp = ((y + 1) % TEX);
      const dx = (lum((y * TEX + xp) * 4) - lum((y * TEX + xm) * 4)) * strength;
      const dy = (lum((yp * TEX + x) * 4) - lum((ym * TEX + x) * 4)) * strength;
      const nx = -dx / 255;
      const ny = -dy / 255;
      const nz = Math.sqrt(Math.max(0.001, 1 - nx * nx - ny * ny));
      dst.data[i] = (nx * 0.5 + 0.5) * 255;
      dst.data[i + 1] = (ny * 0.5 + 0.5) * 255;
      dst.data[i + 2] = nz * 255;
      dst.data[i + 3] = 255;
    }
  }
  ctx.putImageData(dst, 0, 0);
  return c;
}

export function buildRoughnessCanvas(albedo: HTMLCanvasElement, profile: MaterialProfile): HTMLCanvasElement {
  const baseRough: Record<MaterialProfile, number> = {
    wood: 0.58,
    stone: 0.72,
    metal: 0.24,
    paint: 0.52,
    glass: 0.12,
    mirror: 0.05,
    appliance: 0.4,
    fabric: 0.86,
  };
  const c = makeCanvas();
  const ctx = getCtx(c);
  const src = getCtx(albedo, { readback: true }).getImageData(0, 0, TEX, TEX);
  const dst = ctx.createImageData(TEX, TEX);
  const variation = profile === 'wood' ? 0.04 : profile === 'metal' ? 0.18 : 0.28;
  const base = baseRough[profile];
  for (let i = 0; i < src.data.length; i += 4) {
    const lum = (0.2126 * src.data[i] + 0.7152 * src.data[i + 1] + 0.0722 * src.data[i + 2]) / 255;
    const v = Math.max(0, Math.min(1, base + (lum - 0.5) * variation));
    const g = Math.round(v * 255);
    dst.data[i] = g;
    dst.data[i + 1] = g;
    dst.data[i + 2] = g;
    dst.data[i + 3] = 255;
  }
  ctx.putImageData(dst, 0, 0);
  return c;
}

export function buildMetalnessCanvas(albedo: HTMLCanvasElement, profile: MaterialProfile): HTMLCanvasElement {
  const c = makeCanvas();
  const ctx = getCtx(c);
  const dst = ctx.createImageData(TEX, TEX);
  if (profile === 'metal') {
    const src = getCtx(albedo, { readback: true }).getImageData(0, 0, TEX, TEX);
    for (let i = 0; i < dst.data.length; i += 4) {
      const lum = (0.2126 * src.data[i] + 0.7152 * src.data[i + 1] + 0.0722 * src.data[i + 2]) / 255;
      const m = 0.68 + (lum - 0.5) * 0.28;
      const g = Math.round(Math.max(0, Math.min(1, m)) * 255);
      dst.data[i] = dst.data[i + 1] = dst.data[i + 2] = g;
      dst.data[i + 3] = 255;
    }
  } else {
    const flat = profile === 'appliance' ? 92 : 8;
    for (let i = 0; i < dst.data.length; i += 4) {
      dst.data[i] = dst.data[i + 1] = dst.data[i + 2] = flat;
      dst.data[i + 3] = 255;
    }
  }
  ctx.putImageData(dst, 0, 0);
  return c;
}

export function canvasToTexture(canvas: HTMLCanvasElement, opts?: { srgb?: boolean; repeat?: number; rotation?: number }): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  if (opts?.repeat && opts.repeat > 0) t.repeat.set(opts.repeat, opts.repeat);
  if (opts?.rotation) {
    t.center.set(0.5, 0.5);
    t.rotation = opts.rotation;
  }
  if (opts?.srgb) (t as any).colorSpace = (THREE as any).SRGBColorSpace;
  t.generateMipmaps = true;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.anisotropy = 4;
  t.needsUpdate = true;
  return t;
}

function baseColorFor(profile: MaterialProfile, baseHex?: string): string {
  if (baseHex && /^#[0-9a-f]{6}$/i.test(baseHex)) return baseHex;
  switch (profile) {
    case 'wood':
      return '#b98a5f';
    case 'stone':
      return '#c7c3b8';
    case 'metal':
      return '#a8adb5';
    case 'paint':
      return '#e8e5dd';
    case 'glass':
      return '#9cc7d5';
    case 'appliance':
      return '#2e3236';
    case 'fabric':
      return '#a99078';
    default:
      return '#cccccc';
  }
}

function colorTone(hex: string): { l: number; s: number } {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { l, s };
}
